/**
 * `SearchResultsPanel` — the results popover that drops below the topbar
 * search field.
 *
 * ── WHY THIS IS NOT A DIALOG ───────────────────────────────────────────────
 * It replaced a centered modal with a scrim. That pattern is right for a ⌘K
 * palette with NO visible trigger — the overlay is what tells you a new mode
 * opened. We have a visible control that already looks like a search field, so
 * clicking it and getting a *second* search field in the middle of the screen
 * behind a dimmed page is a bait-and-switch: the thing you clicked is not the
 * thing you type in.
 *
 * So: anchored under the trigger, no scrim, no repositioning, and the input
 * stays where it always was. This component renders ONLY the listbox — the
 * input lives in `SearchTrigger`, which is the combobox proper.
 *
 * Semantics follow the WAI-ARIA combobox/listbox pattern: the input carries
 * `role="combobox"` + `aria-expanded`/`aria-controls`/`aria-activedescendant`,
 * and this panel is the `role="listbox"` it points at. There is deliberately no
 * `role="dialog"` and no `aria-modal` anywhere — leaving those behind would
 * tell a screen reader the rest of the page had gone inert when it has not.
 *
 * Positioning: `absolute right-0 top-full`, i.e. anchored to the trigger's
 * bottom-right. Right-aligned because the trigger sits at the right end of the
 * topbar (`ml-auto`); left-aligning would let a panel wider than the trigger
 * run off the viewport. `min-width` is the trigger's own width so it always
 * reads as an extension of that control, never narrower than it.
 */
import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { SEARCH_MIN_QUERY_LENGTH, useGlobalSearch } from "@/hooks/queries/search/useGlobalSearch";
import { useNavDestinations } from "@/components/v2/search/navDestinations";
import {
  useSearchResults,
  type PaletteGroup,
  type PaletteRow,
} from "@/components/v2/search/useSearchResults";

/** DOM-id prefix for options, so `aria-activedescendant` can address them. */
export const OPTION_ID_PREFIX = "cmdk-opt-";

/** The listbox's DOM id — the input's `aria-controls` target. */
export const LISTBOX_ID = "search-results-listbox";

interface SearchResultsControllerProps {
  /** Live query — drives the client-side groups on the first keystroke. */
  query: string;
  /** Debounced query — drives the server request. */
  debounced: string;
  base: string;
  activeIndex: number;
  /** Lifts the flattened rows so the input can run keyboard navigation. */
  onRowsChange: (rows: PaletteRow[]) => void;
  onHoverRow: (index: number) => void;
  onSelectRow: (row: PaletteRow) => void;
}

/**
 * Mounts only while the popover is open — which is the whole point.
 *
 * The input lives in the topbar permanently now, so if IT owned
 * `useGlobalSearch` and `useNavDestinations` (the latter reaches `useMe`),
 * every v2 page would carry two React Query subscriptions for a search box
 * nobody has clicked — and `AppShellV2` would require a `QueryClientProvider`
 * it never needed before, which is a real coupling change, not just a test
 * inconvenience. Keeping the data hooks behind the open gate preserves the
 * property the modal had for free.
 */
export function SearchResultsController({
  query,
  debounced,
  base,
  activeIndex,
  onRowsChange,
  onHoverRow,
  onSelectRow,
}: SearchResultsControllerProps): JSX.Element {
  const destinations = useNavDestinations();
  const server = useGlobalSearch(debounced);
  const { groups, flatRows } = useSearchResults(query, destinations, server.data, base);

  // `useSearchResults` is memoised, so this fires only when the rows actually
  // change identity — no render loop.
  useEffect(() => {
    onRowsChange(flatRows);
  }, [flatRows, onRowsChange]);

  const trimmed = query.trim();
  return (
    <SearchResultsPanel
      groups={groups}
      flatRows={flatRows}
      activeIndex={activeIndex}
      onHoverRow={onHoverRow}
      onSelectRow={onSelectRow}
      hint={trimmed.length === 0}
      loading={trimmed.length >= SEARCH_MIN_QUERY_LENGTH && (server.isPending || server.isFetching)}
      failed={server.isError}
      belowMinimum={trimmed.length > 0 && trimmed.length < SEARCH_MIN_QUERY_LENGTH}
      query={query}
    />
  );
}

interface SearchResultsPanelProps {
  groups: PaletteGroup[];
  flatRows: PaletteRow[];
  activeIndex: number;
  onHoverRow: (index: number) => void;
  onSelectRow: (row: PaletteRow) => void;
  /** Rendered when the query is still empty. */
  hint: boolean;
  loading: boolean;
  failed: boolean;
  belowMinimum: boolean;
  query: string;
}

function SearchResultsPanel({
  groups,
  flatRows,
  activeIndex,
  onHoverRow,
  onSelectRow,
  hint,
  loading,
  failed,
  belowMinimum,
  query,
}: SearchResultsPanelProps): JSX.Element {
  return (
    <div
      id={LISTBOX_ID}
      role="listbox"
      aria-label="Search results"
      data-testid="search-results-panel"
      className={cn(
        "absolute right-0 top-full z-50 mt-1.5 max-h-[70vh] overflow-y-auto",
        "rounded-[var(--radius,0.5rem)] border border-border-default",
        "bg-surface-elevated py-2 shadow-lg",
      )}
      // min-width ties the panel to the control it belongs to; max-width keeps
      // it on-screen at narrow viewports, where the topbar itself is tight.
      style={{ minWidth: "256px", width: "380px", maxWidth: "calc(100vw - 32px)" }}
    >
      {hint ? (
        <PanelNote>
          Search pages, candidates, jobs, and help articles. ↑ ↓ to move, Enter to open.
        </PanelNote>
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <p
                className="text-text-tertiary"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  padding: "10px 14px 4px",
                }}
              >
                {group.label}
              </p>
              {group.rows.map((row) => {
                const index = flatRows.indexOf(row);
                const isActive = index === activeIndex;
                return (
                  <div
                    key={row.id}
                    id={`${OPTION_ID_PREFIX}${row.id}`}
                    role="option"
                    aria-selected={isActive}
                    data-active={isActive ? "true" : "false"}
                    data-testid="search-result-option"
                    onMouseEnter={() => onHoverRow(index)}
                    // `onMouseDown` + preventDefault, NOT onClick: a click would
                    // first blur the input, and the outside-click handler would
                    // close the panel before the click landed on the row.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelectRow(row);
                    }}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 px-3.5 py-2",
                      isActive ? "bg-surface-card-hover" : "bg-transparent",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        data-testid="search-result-title"
                        className={cn("truncate text-text-primary", isActive && "font-semibold")}
                        style={{ fontSize: "13.5px" }}
                      >
                        {row.title}
                      </div>
                      {row.subtitle !== undefined ? (
                        <div className="truncate text-text-tertiary" style={{ fontSize: "12px" }}>
                          {row.subtitle}
                        </div>
                      ) : null}
                    </div>
                    {row.meta !== undefined ? (
                      <span
                        className="shrink-0 rounded-[5px] border border-border-default bg-surface-subtle text-text-secondary"
                        style={{ fontSize: "10.5px", padding: "1px 5px", lineHeight: 1.4 }}
                      >
                        {row.meta}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}

          {/*
           * Status rows sit BELOW the results, never instead of them: the
           * client-side groups are already correct while the server call is in
           * flight or has failed, so they stay on screen and the note explains
           * only what is missing.
           */}
          {failed ? (
            <PanelNote tone="error" testId="search-results-error">
              Couldn’t search candidates and jobs. Pages and help articles are still shown.
            </PanelNote>
          ) : loading ? (
            <PanelNote testId="search-results-loading">Searching candidates and jobs…</PanelNote>
          ) : belowMinimum ? (
            <PanelNote testId="search-results-min-length">
              Type 2 characters to search candidates and jobs.
            </PanelNote>
          ) : null}

          {flatRows.length === 0 && !loading && !failed ? (
            <PanelNote testId="search-results-empty">No results for “{query.trim()}”.</PanelNote>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Muted single-line note for the hint / loading / error / empty states. */
function PanelNote({
  children,
  tone = "muted",
  testId,
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
  testId?: string;
}): JSX.Element {
  return (
    <p
      role="status"
      data-testid={testId}
      className={cn("px-3.5 py-2", tone === "error" ? "text-status-rejected-fg" : "text-text-tertiary")}
      style={{ fontSize: "12px" }}
    >
      {children}
    </p>
  );
}
